import { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { toast } from "sonner";

// SVG icons inline to avoid extra dependencies
function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#00A4EF" d="M13 1h10v10H13z" />
      <path fill="#7FBA00" d="M1 13h10v10H1z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

const REDIRECT_URL = `${window.location.origin}`;

const PROVIDER_OPTIONS = {
  google: { scopes: "email profile" },
  azure:  { scopes: "email openid profile" },
  apple:  { scopes: "email name" },
};

async function signInWithOAuth(provider) {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: REDIRECT_URL,
      scopes: PROVIDER_OPTIONS[provider]?.scopes,
    },
  });
  if (error) toast.error(error.message);
}

export default function AuthPage() {
  const [mode, setMode] = useState("signin"); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) toast.error(error.message);
          else toast.success("Check your email to confirm your account");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Please enter your email"); return; }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${REDIRECT_URL}?reset=1`,
    });
    setLoading(false);
    if (error) toast.error(error.message);
    else toast.success("Check your email to reset your password");
  };

  return (
    <div className="min-h-screen w-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-white">DualView</h1>
          <p className="mt-1 text-sm text-white/40">Professional video comparison tool</p>
        </div>

        {/* Card */}
        <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 space-y-5">

          {/* OAuth buttons */}
          <div className="space-y-2">
            <button
              onClick={() => signInWithOAuth("google")}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white transition-colors"
            >
              <GoogleIcon />
              <span className="flex-1 text-center">Continue with Google</span>
            </button>
            <button
              onClick={() => signInWithOAuth("azure")}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white transition-colors"
            >
              <MicrosoftIcon />
              <span className="flex-1 text-center">Continue with Microsoft</span>
            </button>
            <button
              onClick={() => signInWithOAuth("apple")}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white transition-colors"
            >
              <AppleIcon />
              <span className="flex-1 text-center">Continue with Apple</span>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/30">or with email</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Email form */}
          {mode !== "reset" ? (
            <form onSubmit={handleEmailAuth} className="space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#007AFF] transition-colors"
              />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                minLength={6}
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#007AFF] transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg bg-[#007AFF] hover:bg-[#0066DD] disabled:opacity-50 text-sm font-semibold text-white transition-colors"
              >
                {loading ? "..." : mode === "signup" ? "Create account" : "Sign in"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleReset} className="space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#007AFF] transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg bg-[#007AFF] hover:bg-[#0066DD] disabled:opacity-50 text-sm font-semibold text-white transition-colors"
              >
                {loading ? "..." : "Send reset link"}
              </button>
            </form>
          )}

          {/* Footer links */}
          <div className="flex flex-col items-center gap-1.5 text-xs text-white/40">
            {mode === "signin" && (
              <>
                <button onClick={() => setMode("signup")} className="hover:text-white/70 transition-colors">
                  Don't have an account? <span className="text-[#007AFF]">Sign up</span>
                </button>
                <button onClick={() => setMode("reset")} className="hover:text-white/70 transition-colors">
                  Forgot your password?
                </button>
              </>
            )}
            {mode === "signup" && (
              <button onClick={() => setMode("signin")} className="hover:text-white/70 transition-colors">
                Already have an account? <span className="text-[#007AFF]">Sign in</span>
              </button>
            )}
            {mode === "reset" && (
              <button onClick={() => setMode("signin")} className="hover:text-white/70 transition-colors">
                ← Back to sign in
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
